import { c as _c } from "react/compiler-runtime";
import { useKeyboardEvent } from '@react-hookz/web/useKeyboardEvent/index.js';
import Image from 'next/image';
import type { ChangeEvent } from 'react';
import { useRef, useEffect, useCallback, useId } from 'react';
import { useFocusInside } from '#cutils/hooks/useFocusInside';
import { RadioButtonIcon } from './RadioButtonIcon';
import styles from './ThemeSelector.module.scss';
import ThemeDarkIcon from './icons/ThemeDarkIcon.svg';
import ThemeLightIcon from './icons/ThemeLightIcon.svg';
import ThemeSystemIcon from './icons/ThemeSystemIcon.svg';
const OptionsKeyboardEvents = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);
const Themes = [{
  image: <Image className={styles.radioImage} src={ThemeLightIcon} alt="Helles Farbschema" unoptimized={true} />,
  name: 'Hell',
  value: 'light' as const,
  ariaLabel: 'Helles Farbschema'
}, {
  image: <Image className={styles.radioImage} src={ThemeDarkIcon} alt="Dunkles Farbschema" unoptimized={true} />,
  name: 'Dunkel',
  value: 'dark' as const,
  ariaLabel: 'Dunkles Farbschema'
}, {
  image: <Image className={styles.radioImage} src={ThemeSystemIcon} alt="Systemvorgabe" unoptimized={true} />,
  name: 'System',
  value: 'system' as const,
  ariaLabel: 'Systemvorgabe'
}];
type Props = {
  className?: string;
  theme: string | undefined;
  onThemeChange?: (theme: string) => void;
  forceTheme?: 'light' | 'dark';
};
export function ThemeSelector(t0) {
  const $ = _c(27);
  const {
    className,
    theme,
    onThemeChange,
    forceTheme
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const optionsRef = useRef(t1);
  const id = useId();
  let t2;
  if ($[1] !== theme) {
    t2 = t3 => {
      const {
        value
      } = t3;
      return value === theme;
    };
    $[1] = theme;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const selectedThemeIndex = Themes.findIndex(t2);
  const {
    focused: themeSelectorIsFocused,
    ref: themeSelectorRef
  } = useFocusInside();
  let t3;
  if ($[3] !== themeSelectorIsFocused) {
    t3 = event => themeSelectorIsFocused && OptionsKeyboardEvents.has(event.code);
    $[3] = themeSelectorIsFocused;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onThemeChange || $[6] !== selectedThemeIndex) {
    t4 = e => {
      const lastChildIndex = Themes.length - 1;
      let themeToActivate = selectedThemeIndex;
      bb10: switch (e.code) {
        case "ArrowLeft":
          {
            themeToActivate = Math.max(selectedThemeIndex - 1, 0);
            break bb10;
          }
        case "ArrowRight":
          {
            themeToActivate = Math.min(selectedThemeIndex + 1, lastChildIndex);
            break bb10;
          }
        case "Home":
          {
            themeToActivate = 0;
            break bb10;
          }
        case "End":
          {
            themeToActivate = lastChildIndex;
          }
      }
      onThemeChange?.(Themes[themeToActivate].value);
      e.stopPropagation();
      e.preventDefault();
    };
    $[5] = onThemeChange;
    $[6] = selectedThemeIndex;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onThemeChange || $[9] !== selectedThemeIndex || $[10] !== themeSelectorIsFocused) {
    t5 = [themeSelectorIsFocused, selectedThemeIndex, onThemeChange];
    $[8] = onThemeChange;
    $[9] = selectedThemeIndex;
    $[10] = themeSelectorIsFocused;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useKeyboardEvent(t3, t4, t5);
  let t6;
  let t7;
  if ($[12] !== selectedThemeIndex || $[13] !== themeSelectorIsFocused) {
    t6 = () => {
      if (themeSelectorIsFocused) {
        optionsRef.current[selectedThemeIndex]?.focus();
      }
    };
    t7 = [selectedThemeIndex, themeSelectorIsFocused];
    $[12] = selectedThemeIndex;
    $[13] = themeSelectorIsFocused;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  if ($[16] !== onThemeChange) {
    t8 = e_0 => {
      onThemeChange?.(e_0.target.value);
    };
    $[16] = onThemeChange;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const changeTheme = t8;
  const t9 = `${className ?? ""} ${styles.themeSelector}`;
  let t10;
  if ($[18] !== changeTheme || $[19] !== id || $[20] !== selectedThemeIndex) {
    t10 = Themes.map((t11, index) => {
      const {
        value: value_0,
        image,
        name,
        ariaLabel
      } = t11;
      const isSelected = selectedThemeIndex === index;
      const radioId = `${id}-${value_0}`;
      return <li className={styles.themeItem} key={value_0}><label htmlFor={radioId} className={isSelected ? `${styles.selected} body1` : ""} tabIndex={isSelected ? 0 : -1} ref={label => {
          optionsRef.current[index] = label;
        }} aria-label={ariaLabel}>{image}<input type="radio" value={value_0} id={radioId} name="theme" checked={isSelected} onChange={e_1 => changeTheme(e_1)} tabIndex={-1} /><RadioButtonIcon className={styles.checkbox} selected={isSelected} /><span className={styles.radioLabel}>{name}</span></label></li>;
    });
    $[18] = changeTheme;
    $[19] = id;
    $[20] = selectedThemeIndex;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== forceTheme || $[23] !== t10 || $[24] !== t9 || $[25] !== themeSelectorRef) {
    t11 = <ul className={t9} ref={themeSelectorRef} data-theme={forceTheme}>{t10}</ul>;
    $[22] = forceTheme;
    $[23] = t10;
    $[24] = t9;
    $[25] = themeSelectorRef;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}